import React, { useState } from "react";
import {
  ArrowUpRight,
  Building2,
  Layers,
  DoorOpen,
  Sparkles,
  ShieldCheck,
  BadgeDollarSign,
  Factory,
} from "lucide-react";
import Header from "../components/site/Header";
import Footer from "../components/site/Footer";
import UtilityBar from "../components/site/UtilityBar";
import QuoteDialog from "../components/site/QuoteDialog";
import CatalogDialog from "../components/site/CatalogDialog";
import AccessibilityPanel from "../components/site/AccessibilityPanel";
import MobileStickyBar from "../components/site/MobileStickyBar";
import ResponsiveImg from "../components/site/ResponsiveImg";
import SeoHead from "../components/site/SeoHead";
import FaqSection from "../components/site/FaqSection";
import BreadcrumbSchema from "../components/site/BreadcrumbSchema";

const SYSTEMS = [
  {
    Icon: Layers,
    code: "VLX-CW · CURTAIN WALL",
    title: "Curtain Wall Systems",
    desc:
      "Floor-to-floor structural glazing with slim sightlines for office towers, civic, and mixed-use projects. Engineered for high seismic and wind-load zones.",
    img: "/viewlux/commercial/curved-tower.jpg",
  },
  {
    Icon: Building2,
    code: "VLX-SF · STOREFRONT",
    title: "Storefront Glazing",
    desc:
      "Versatile center-set or offset framing for retail, hospitality, and ground-floor commercial. Available in standard and impact-rated configurations.",
    img: "/viewlux/commercial/modern-office.jpg",
  },
  {
    Icon: DoorOpen,
    code: "VLX-EN · ENTRANCES",
    title: "Entrance Systems",
    desc:
      "Heavy-duty commercial swing, pivot, and automatic sliding entrances. Built for daily traffic with reinforced corner blocks and concealed closers.",
    img: "/viewlux/commercial/concrete-glass.jpg",
  },
  {
    Icon: Sparkles,
    code: "VLX-WW · WINDOW WALL",
    title: "Unitized Window Wall",
    desc:
      "Pre-glazed, factory-assembled panels for fast on-site installation in multi-family and hospitality high-rises. Reduces install time by 40%.",
    img: "/viewlux/commercial/shard-tower.jpg",
  },
];

const VALUE_POINTS = [
  {
    Icon: Factory,
    title: "Work Directly With The Manufacturer",
    body: "Skip the spec rep chain. Talk to our commercial engineering team from RFP through punch-list.",
  },
  {
    Icon: BadgeDollarSign,
    title: "Save Up To 50% On Glazing Packages",
    body: "Factory-direct pricing on every commercial system. No distributor markup, no rep commissions.",
  },
  {
    Icon: ShieldCheck,
    title: "NFRC & AAMA Certified",
    body: "Every commercial assembly carries certified U-factor, SHGC, structural, water, and air ratings.",
  },
];

export default function CommercialPage() {
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);

  return (
    <div data-testid="commercial-page" className="bg-cream pb-16 md:pb-0 font-nunito">
      <SeoHead
        title="Commercial Aluminum Systems — Curtain Wall, Storefronts, Entrances | ViewLux"
        description="ViewLux Commercial — aluminum curtain wall, storefront, and architectural entrance systems for office, retail, hospitality, and civic projects across Colorado."
        path="/commercial"
      />
      <BreadcrumbSchema
        testId="commercial"
        items={[
          { name: "Home", url: "https://viewluxcolorado.com/" },
          { name: "Commercial Systems", url: "https://viewluxcolorado.com/commercial" },
        ]}
      />
      <UtilityBar />
      <Header
        onRequestQuote={() => setQuoteOpen(true)}
        onDownloadCatalog={() => setCatalogOpen(true)}
      />

      {/* Hero */}
      <section
        data-testid="commercial-hero"
        className="relative bg-[#0f2420] text-white overflow-hidden"
      >
        <div className="absolute inset-0">
          <ResponsiveImg
            src="/viewlux/commercial/curved-tower.jpg"
            alt="Curved glass commercial tower with city skyline reflections"
            sizes="100vw"
            className="absolute inset-0 w-full h-full object-cover opacity-55"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0f2420] via-[#0f2420]/70 to-[#0f2420]/30" />
        </div>
        <div className="relative container-vlx pt-44 md:pt-52 pb-24 md:pb-36">
          <div className="max-w-3xl">
            <span className="eyebrow eyebrow-light">Commercial &amp; Architectural</span>
            <div className="mt-4 rule-gold" />
            <h1 className="heading-lg mt-8 text-[42px] md:text-[72px] leading-[1.02] text-white">
              Aluminum systems for{" "}
              <em className="italic font-light text-[#e2b880]">
                architects &amp; builders.
              </em>
            </h1>
            <p className="mt-8 text-white/75 text-[15.5px] md:text-[17px] font-nunito leading-[1.85] max-w-xl">
              ViewLux engineers thermally broken aluminum curtain walls,
              storefronts, window walls, and entrance systems for commercial,
              hospitality, multi-family, and civic projects across the United
              States. Factory-direct pricing. Full architectural customization.
              Certified performance.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <button
                data-testid="commercial-cta-quote"
                onClick={() => setQuoteOpen(true)}
                className="btn-pill btn-primary"
              >
                Request a Commercial Quote
              </button>
              <button
                data-testid="commercial-cta-catalog"
                onClick={() => setCatalogOpen(true)}
                className="btn-pill btn-ghost-light"
              >
                Download Catalog
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Value bar */}
      <section className="bg-[#16332d] text-white border-b border-[#c99658]/15">
        <div className="container-vlx py-14 md:py-20">
          <ul className="grid md:grid-cols-3 gap-px bg-[#c99658]/15">
            {VALUE_POINTS.map((p, i) => (
              <li
                key={p.title}
                data-testid={`commercial-value-${i}`}
                className="bg-[#16332d] p-8 md:px-10 md:py-12 flex flex-col gap-5"
              >
                <div className="flex items-center gap-4">
                  <span className="w-12 h-12 inline-flex items-center justify-center rounded-full border border-[#c99658]/40 text-[#e2b880]">
                    <p.Icon className="w-5 h-5" strokeWidth={1.5} />
                  </span>
                  <span className="text-[10.5px] tracking-[0.45em] uppercase font-raleway text-[#e2b880]">
                    0{i + 1}
                  </span>
                </div>
                <h2 className="heading-lg text-[20px] md:text-[24px] text-white leading-[1.2]">
                  {p.title}
                </h2>
                <p className="text-white/65 text-[14px] leading-[1.85]">
                  {p.body}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Systems */}
      <section
        data-testid="commercial-systems"
        id="systems"
        className="bg-cream"
      >
        <div className="container-vlx py-24 md:py-32">
          <div className="max-w-2xl">
            <span className="eyebrow">Commercial Systems</span>
            <div className="mt-4 rule-gold" />
            <h2 className="heading-lg mt-8 text-[36px] md:text-[56px] leading-[1.05] text-[#0f2420]">
              Engineered for{" "}
              <em className="italic font-light text-[#7a5b2f]">
                the way buildings are actually built.
              </em>
            </h2>
            <p className="mt-8 text-neutral-700 text-[15px] leading-[1.9] max-w-xl">
              From single-story storefronts to twenty-story curtain wall
              envelopes, our commercial line is designed for predictable
              installation, easy specification, and long service life. Every
              system is fully customizable in size, depth, finish, and glass
              package.
            </p>
          </div>

          <ul className="mt-16 md:mt-24 grid md:grid-cols-2 gap-px bg-[#c99658]/25">
            {SYSTEMS.map((s, i) => (
              <li
                key={s.code}
                data-testid={`commercial-system-${i}`}
                className="bg-cream flex flex-col group"
              >
                <div className="relative aspect-[4/3] bg-[#0f2420] overflow-hidden">
                  <ResponsiveImg
                    src={s.img}
                    alt={s.title}
                    sizes="(max-width: 768px) 100vw, 50vw"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[2200ms] ease-out group-hover:scale-[1.04]"
                  />
                  <span className="absolute top-5 left-5 text-[10px] tracking-[0.4em] uppercase font-raleway text-[#e2b880] bg-[#0f2420]/70 backdrop-blur px-3 py-1.5">
                    {s.code}
                  </span>
                </div>
                <div className="p-7 md:p-10">
                  <div className="flex items-center gap-3 text-[#7a5b2f]">
                    <s.Icon className="w-4 h-4" strokeWidth={1.6} />
                    <span className="text-[10.5px] tracking-[0.4em] uppercase font-raleway">
                      0{i + 1}
                    </span>
                  </div>
                  <h3 className="heading-lg mt-4 text-[24px] md:text-[28px] text-[#0f2420] leading-[1.15]">
                    {s.title}
                  </h3>
                  <p className="mt-4 text-neutral-700 text-[14px] leading-[1.85] max-w-md">
                    {s.desc}
                  </p>
                  <button
                    onClick={() => setQuoteOpen(true)}
                    data-testid={`commercial-system-cta-${i}`}
                    className="link-arrow mt-6"
                    aria-label={`Request commercial quote for ${s.title}`}
                  >
                    Spec This System
                    <ArrowUpRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Capabilities strip */}
      <section className="bg-[#0f2420] text-white border-y border-[#c99658]/15">
        <div className="container-vlx py-20 md:py-24">
          <div className="grid md:grid-cols-12 gap-12 md:gap-16 items-start">
            <div className="md:col-span-5">
              <span className="eyebrow eyebrow-light">Capabilities</span>
              <div className="mt-4 rule-gold" />
              <h2 className="heading-lg mt-8 text-[28px] md:text-[42px] text-white leading-[1.1]">
                Fully{" "}
                <em className="italic font-light text-[#e2b880]">
                  custom-made
                </em>{" "}
                solutions.
              </h2>
              <p className="mt-6 text-white/65 text-[14.5px] leading-[1.9] max-w-md">
                Every commercial project is engineered to the building &mdash;
                not the other way around. We respect your architectural
                vision, your schedule, and your budget.
              </p>
            </div>
            <ul className="md:col-span-7 grid sm:grid-cols-2 gap-x-10 gap-y-6">
              {[
                "Custom shapes — arches, corners, raked tops, curved facades",
                "Spans up to 20+ feet without intermediate mullions",
                "Powder-coat & anodized finishes — full RAL system",
                "Standard, impact-rated, and blast-resistant glass packages",
                "Thermally broken aluminum — meets all U.S. energy codes",
                "Shop drawings, structural calcs & specs provided",
                "Architectural-grade hardware from FritsJurgens, Roto, Storo",
                "Direct factory pricing on every line item",
              ].map((cap) => (
                <li
                  key={cap}
                  className="flex items-start gap-3 text-white/75 text-[13.5px] leading-[1.7]"
                >
                  <span
                    aria-hidden="true"
                    className="mt-2 w-1.5 h-1.5 bg-[#e2b880] flex-shrink-0"
                  />
                  {cap}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Project gallery */}
      <section className="bg-cream">
        <div className="container-vlx py-24 md:py-32">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div className="max-w-xl">
              <span className="eyebrow">Recent Envelopes</span>
              <div className="mt-4 rule-gold" />
              <h2 className="heading-lg mt-8 text-[30px] md:text-[44px] text-[#0f2420] leading-[1.1]">
                A few projects we&rsquo;ve been{" "}
                <em className="italic font-light text-[#7a5b2f]">part of.</em>
              </h2>
            </div>
            <p className="text-neutral-700 text-[14px] leading-[1.85] max-w-sm">
              Civic, hospitality, office, and multi-family envelopes across
              Colorado, Pennsylvania, and the wider United States.
            </p>
          </div>

          <ul className="mt-16 grid md:grid-cols-3 gap-6 md:gap-8">
            {[
              { src: "/viewlux/commercial/curved-tower.jpg", caption: "Curved Curtain Wall · High-Rise" },
              { src: "/viewlux/commercial/shard-tower.jpg", caption: "Angled Spandrel · Tower Facade" },
              { src: "/viewlux/commercial/modern-office.jpg", caption: "Two-Story Office · Curtain Wall" },
              { src: "/viewlux/commercial/concrete-glass.jpg", caption: "Civic · Glass &amp; Concrete" },
              { src: "/viewlux/commercial/industrial-peak.jpg", caption: "Industrial · Architectural Peak" },
              { src: "/viewlux/commercial/tower-skyline.jpg", caption: "Mixed-Use Tower · Skyline" },
              { src: "/viewlux/commercial/green-tower.jpg", caption: "Civic · Accent Color Glazing" },
            ].slice(0, 7).map((g, i) => (
              <li
                key={i}
                data-testid={`commercial-gallery-${i}`}
                className={`relative overflow-hidden aspect-[4/5] bg-[#0f2420] group ${
                  i === 0 ? "md:col-span-2 md:row-span-2 md:aspect-auto" : ""
                }`}
              >
                <ResponsiveImg
                  src={g.src}
                  alt={g.caption}
                  sizes="(max-width: 768px) 100vw, 33vw"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[2500ms] ease-out group-hover:scale-[1.05]"
                />
                <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-[#0f2420]/85 to-transparent">
                  <span className="text-[10.5px] tracking-[0.3em] uppercase font-raleway text-[#e2b880]">
                    {g.caption}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Closing CTA */}
      {/* SEO LONG-FORM + FAQ */}
      <section className="bg-white border-t border-[#c99658]/15">
        <div className="container-vlx py-20 md:py-28 max-w-4xl">
          <span className="eyebrow">The Commercial Brief</span>
          <div className="mt-4 rule-gold" />
          <h2 className="heading-lg mt-8 text-[30px] md:text-[44px] text-[#0f2420] leading-[1.12]">
            Aluminum curtain wall, storefront &amp; entrance systems{" "}
            <em className="italic font-light text-[#7a5b2f]">
              for Colorado projects.
            </em>
          </h2>
          <div className="mt-8 space-y-5 text-[15px] leading-[1.95] text-neutral-700 font-nunito">
            <p>
              ViewLux Commercial supplies thermally broken aluminum envelope
              systems for office, retail, hospitality, multifamily, civic, and
              mixed-use projects across Colorado and the Rocky Mountain region.
              The core line includes <strong>VLX-CW curtain wall</strong>{" "}
              (floor-to-floor structural glazing engineered for high seismic
              and wind-load zones), <strong>VLX-SF storefront</strong> (center-set
              or offset framing for ground-floor commercial), and{" "}
              <strong>VLX-EN entrance systems</strong> (heavy-duty swing,
              pivot, and automatic sliding entrances with reinforced corner
              blocks and concealed closers).
            </p>
            <p>
              Every aluminum system is rated to <strong>AAMA Gold Label</strong>{" "}
              and <strong>NFRC</strong> certified performance for structural,
              water, air, and thermal cycling — independently tested at Molimo
              Labs. Custom finishes include the full RAL Classic spectrum in
              powder-coated, anodized, and dual-tone (different interior vs
              exterior) configurations. We provide AIA-supported product data
              sheets, CSI 08 41 00 specifications, BIM and Revit families,
              shop drawings, and on-site engineering support during install.
            </p>
            <p>
              ViewLux ships factory-direct from Frederick, Colorado, which
              shortens lead times for Front-Range and Western-Slope projects
              compared to coastal manufacturers. Architects and GCs working on
              residential luxury projects should also see our{" "}
              <a href="/doors" className="underline decoration-[#c99658] decoration-2 underline-offset-4 hover:text-[#7a5b2f]">
                Custom Doors collection
              </a>{" "}
              for entrance hardware, pivot systems, and interior series, or our{" "}
              <a href="/" className="underline decoration-[#c99658] decoration-2 underline-offset-4 hover:text-[#7a5b2f]">
                full window collection
              </a>{" "}
              for residential aluminum window systems.
            </p>
          </div>
        </div>
      </section>

      <FaqSection
        testId="commercial-faq"
        eyebrow="Frequently Asked"
        title={
          <>
            Commercial specification questions,{" "}
            <em className="italic font-light text-[#7a5b2f]">answered.</em>
          </>
        }
        intro="Common questions from architects, GCs, and developers specifying ViewLux commercial systems on Colorado projects."
        items={[
          {
            q: "What is the difference between curtain wall and storefront?",
            a: "Curtain wall is a non-load-bearing exterior wall system that spans multiple floors and is hung from the building structure — typically used on towers and large-footprint commercial buildings. Storefront is a single-story system installed between floor slabs, used for ground-floor retail, lobbies, and hospitality entrances. ViewLux engineers both with thermally broken aluminum framing and integrated structural silicone glazing options.",
          },
          {
            q: "Are ViewLux commercial systems impact-rated or hurricane-resistant?",
            a: "We offer impact-rated storefront and entrance configurations meeting AAMA 506 and ASTM E1886/E1996 standards for high-velocity wind zones. Colorado projects rarely require this rating, but it's available when specified. Standard configurations are rated for the wind and seismic loads typical to Front-Range and Western-Slope construction.",
          },
          {
            q: "What is the lead time for a commercial project?",
            a: "Storefront and standard entrance systems typically ship 10–14 weeks from approved shop drawings. Curtain wall lead time is project-dependent, generally 14–20 weeks. We can usually accelerate when there is a critical-path constraint — call us early in the spec phase and we'll align with your construction schedule.",
          },
          {
            q: "Do you provide BIM, Revit, and CSI specifications?",
            a: "Yes. We provide Revit families, BIM models, AIA-formatted product data sheets, and CSI 08 41 00 (Curtain Wall and Storefront) specifications. Email sales@viewluxcolorado.com with your project requirements and we'll send the current revision.",
          },
          {
            q: "What finishes and colors are available?",
            a: "Every aluminum system is available in the full RAL Classic spectrum in powder-coated, anodized, or dual-tone configurations. Standard architectural finishes include matte black, anthracite, bronze, champagne, silver, and clear-anodized. Custom finishes (woodgrain, metallic, textured) are available on quote.",
          },
          {
            q: "Can ViewLux supply entrance hardware and automatic doors?",
            a: "Yes. Heavy-duty commercial swing entrances ship with reinforced corner blocks, concealed overhead closers, and ADA-compliant operators. We integrate automatic sliding entrances, low-energy operators, and access-control hardware (mag locks, electric strikes, exit devices) as specified by the architect.",
          },
        ]}
      />

      <section className="bg-[#16332d] text-white">
        <div className="container-vlx py-20 md:py-28 text-center">
          <span className="eyebrow eyebrow-light">Specifying ViewLux?</span>
          <div className="mt-4 rule-gold mx-auto" />
          <h2 className="heading-lg mt-8 text-[34px] md:text-[52px] text-white leading-[1.05] max-w-3xl mx-auto">
            Send us your{" "}
            <em className="italic font-light text-[#e2b880]">
              elevations, schedule, and spec book.
            </em>
          </h2>
          <p className="mt-6 text-white/65 text-[14.5px] leading-[1.85] max-w-xl mx-auto">
            We&rsquo;ll respond with a system selection, shop drawings, and a
            line-item budget &mdash; usually within five business days.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <button
              data-testid="commercial-closing-cta"
              onClick={() => setQuoteOpen(true)}
              className="btn-pill btn-primary"
            >
              Start a Commercial Quote
            </button>
            <a
              href="mailto:sales@viewluxcolorado.com"
              className="btn-pill btn-ghost-light"
              data-testid="commercial-email"
            >
              Email the Team
            </a>
          </div>
        </div>
      </section>

      <Footer onDownloadCatalog={() => setCatalogOpen(true)} />

      <QuoteDialog open={quoteOpen} onClose={() => setQuoteOpen(false)} />
      <CatalogDialog open={catalogOpen} onClose={() => setCatalogOpen(false)} />
      <AccessibilityPanel />
      <MobileStickyBar onRequestQuote={() => setQuoteOpen(true)} />
    </div>
  );
}
